/** Shared fetch plumbing: timeouts, abort passthrough, typed errors. */

export class ApiError extends Error {
  constructor(
    message: string,
    /** Torn's numeric error code, when the failure came from its error envelope. */
    readonly code: number | null = null,
    readonly source: "torn" | "weav3r" | "network" = "network",
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const DEFAULT_TIMEOUT_MS = 15_000;

/**
 * Combines an external abort signal with a timeout, so a stuck request can
 * never wedge the poll loop.
 */
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number): {
  signal: AbortSignal;
  done: () => void;
} {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error("Request timed out")), timeoutMs);

  const onAbort = () => controller.abort(signal?.reason);
  if (signal) {
    if (signal.aborted) onAbort();
    else signal.addEventListener("abort", onAbort, { once: true });
  }

  return {
    signal: controller.signal,
    done: () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    },
  };
}

export function isAbort(err: unknown): boolean {
  return err instanceof Error && (err.name === "AbortError" || err.name === "TimeoutError");
}

export async function getJson<T>(
  url: string,
  opts: { signal?: AbortSignal; timeoutMs?: number; source?: "torn" | "weav3r" } = {},
): Promise<T> {
  const source = opts.source ?? "network";
  const { signal, done } = withTimeout(opts.signal, opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch(url, { signal, cache: "no-store", credentials: "omit" });
  } catch (err) {
    if (isAbort(err) || (opts.signal?.aborted ?? false)) throw err;
    const message = err instanceof Error ? err.message : String(err);
    throw new ApiError(`Network error: ${message}`, null, source);
  } finally {
    done();
  }

  if (!response.ok) {
    throw new ApiError(`HTTP ${response.status}`, response.status, source);
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError("Malformed JSON response", null, source);
  }
}
